import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import useInitStore from './init';
import type { Address } from '../types/address';
import type { Contact } from '../types/common';
import type { Household } from '../types/household';
import { OrdererRelationToBuilding, type StepOrdererData } from '../types/step-data';

const relationToBuildingMap = {
  [OrdererRelationToBuilding.IS_OWNER]: '1',
  [OrdererRelationToBuilding.IS_ADMINISTRATOR]: '2',
  [OrdererRelationToBuilding.IS_NOT_OWNER_OR_ADMINISTRATOR]: '3',
};

const useFormOrdererStore = defineStore('form-orderer', () => {
  const initStore = useInitStore();

  const {
    contact: initContact,
    household: initHousehold,
  } = initStore.household ?? {};

  const address = ref<Address>(initStore.paramsAddress!);
  const appointmentDate = ref<string>();
  const contact = ref<Contact | undefined>(initContact ?? undefined);
  const contactingAppointment = ref<boolean>(true);
  const household = ref<Household | undefined>(initHousehold ?? undefined);
  const invoiceAddress = ref<Address>();
  const invoiceAddressIsConnectionAddress = ref<boolean>(false);
  const relationToBuilding = ref<OrdererRelationToBuilding>(OrdererRelationToBuilding.IS_OWNER);
  const isOwnerOrManager = ref<string>('1');
  const notOwnerNeitherManager = ref<boolean>(false);
  const additionalInfo = ref<string>('');
  const buildingHasAdditionalHouseNumbers = ref<boolean>();

  const isOrdererNotOwnerOrAdministrator = computed(
    () => relationToBuilding.value === OrdererRelationToBuilding.IS_NOT_OWNER_OR_ADMINISTRATOR,
  );

  const stepData = computed<StepOrdererData>(() => ({
    address: address.value,
    appointmentDate: appointmentDate.value,
    contact: contact.value,
    contactingAppointment: contactingAppointment.value,
    household: household.value,
    invoiceAddress: invoiceAddressIsConnectionAddress.value ? null : invoiceAddress.value,
    isClientsAddress: invoiceAddressIsConnectionAddress.value,
    isOwnerOrManager: relationToBuildingMap[relationToBuilding.value],
    notOwnerNeitherManager: isOrdererNotOwnerOrAdministrator.value,
    additionalInfo: additionalInfo.value,
    addtionalHouseNumbers: buildingHasAdditionalHouseNumbers.value,
  }) as unknown as StepOrdererData);

  const isNewOrExistingEnergyCustomer = computed(
    () => !household.value?.customerId || household.value.customerId.toString().startsWith('2'),
  );

  watch(invoiceAddressIsConnectionAddress, value => {
    invoiceAddress.value = value ? initStore.paramsAddress! : {
      street: '',
      houseNumber: '',
      suffix: '',
      zipCode: '',
      city: '',
    };
  });

  function addHouseholdId(id: number) {
    if (household.value) {
      household.value.id = id;
    }
  }

  return {
    address,
    appointmentDate,
    buildingHasAdditionalHouseNumbers,
    contact,
    contactingAppointment,
    household,
    invoiceAddress,
    invoiceAddressIsConnectionAddress,
    relationToBuilding,
    isOwnerOrManager,
    notOwnerNeitherManager,
    additionalInfo,
    isOrdererNotOwnerOrAdministrator,
    stepData,
    isNewOrExistingEnergyCustomer,
    addHouseholdId,
  };
});

export default useFormOrdererStore;
